import {
	carrierName,
	contractSuretyTypeSchema,
	currencySchema,
	suretyBondId,
	suretyBondStatusSchema,
	suretyBondTypeSchema,
	suretyQuoteId,
	suretyQuoteStatusSchema,
} from "@inrev/common";
import * as z from "zod";
import { defineTool } from "../tool-definition";

/** Open is bonded, approved is committed but not yet bonded, requested awaits underwriting. */
const exposureStandingSchema = z.enum(["open", "approved", "requested"]);

const exposureCategorySchema = suretyBondTypeSchema.extract(["bid", "final"]);

const exposureExclusionReasonSchema = z.enum([
	"bond_closed",
	"bid_bond_replaced",
	"request_bound",
	"request_declined",
	"request_closed",
]);

const exposureNoteKindSchema = z.enum(["request_without_carrier"]);

const recordSchema = z.enum(["bond", "bond_request"]);
const statusSchema = z.union([suretyBondStatusSchema, suretyQuoteStatusSchema]);

const standingTotalsSchema = z.strictObject({
	open: currencySchema,
	approved: currencySchema,
	requested: currencySchema,
});

const itemSchema = z.strictObject({
	record: recordSchema,
	id: z.union([suretyBondId, suretyQuoteId]),
	bondNumber: z.string().nullable(),
	type: contractSuretyTypeSchema,
	status: statusSchema,
	carrierName: carrierName.nullable(),
	category: exposureCategorySchema.nullable(),
	standing: exposureStandingSchema,
	amount: currencySchema,
});

const excludedSchema = z.strictObject({
	record: recordSchema,
	id: z.union([suretyBondId, suretyQuoteId]),
	bondNumber: z.string().nullable(),
	type: contractSuretyTypeSchema,
	status: statusSchema,
	carrierName: carrierName.nullable(),
	amount: currencySchema,
	reason: exposureExclusionReasonSchema,
	relatedBondId: suretyBondId.nullable(),
	relatedBondRequestIds: z.array(suretyQuoteId),
});

export const deriveOpenExposure = defineTool({
	name: "derive_open_exposure",
	description:
		"The account's exposure as the account page's exposure card shows it, computed by the same code, so quote these figures rather than adding up `list_bonds` or `list_bond_requests`. Every counted record has a `standing`: `open` is an open bond, `approved` a request that is approved, pending or accepted but not yet bound, `requested` a request still in underwriting review. `used` is open plus approved, the capacity the account has taken; `requested` is not counted against capacity because no one has committed to it. `byCategory` splits open, approved and requested into bid and final; a bid-to-final request counts as final. `byCarrier` is each carrier's open and approved. `limit` is the account's bonding line and `available` its aggregate limit less `used`, negative when over; it is null when the account has no bonding line. Per-carrier bonding-line usage is not here. A bid bond leaves exposure when an accepted, approved, or pending bid-to-final names it (`bid_bond_replaced`, with the request in `relatedBondRequestIds`), so the job is counted once. A bid-to-final in review stays requested and its bid bond stays open. `items` is every record counted and `excluded` every record left out, each with its `reason`; a bond's row carries its `bondNumber`, a request's none; a bound request's `relatedBondId` is the bond it became. `notes` names requests with no carrier, which count toward `byCategory` but no carrier.",
	label: "Open exposure",
	contexts: ["account", "bond_request", "bond"],
	implementation: "api",
	audience: "model",
	input: z.strictObject({}),
	data: z.strictObject({
		used: currencySchema,
		requested: currencySchema,
		byCategory: z.strictObject({
			bid: standingTotalsSchema,
			final: standingTotalsSchema,
		}),
		byCarrier: z.array(
			z.strictObject({ carrierName, open: currencySchema, approved: currencySchema }),
		),
		limit: z
			.strictObject({
				singleLimit: currencySchema,
				aggregateLimit: currencySchema,
				available: currencySchema,
			})
			.nullable(),
		items: z.array(itemSchema),
		excluded: z.array(excludedSchema),
		notes: z.array(
			z.strictObject({
				kind: exposureNoteKindSchema,
				bondRequestIds: z.array(suretyQuoteId),
			}),
		),
	}),
});
